import {
  Banner,
  Card,
  Layout,
  Modal,
  Page,
  Spinner,
  Tabs,
  TextContainer,
} from "@shopify/polaris";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAdmin } from "../../context/admin";
import { fetchData, postData } from "../../util/fetch";
import ChangeSummary from "./components/ChangeSummary";
import ErrorSummary from "./components/ErrorSummary";
import ImportDetails from "./components/ImportDetails";
import ImportHistoryTable from "./components/ImportHistoryTable";
import ImportStats from "./components/ImportStats";

const HdsImportHistoryPage = () => {
  const { selectedShopId } = useAdmin();
  const [activeTab, setActiveTab] = useState(0);
  const [importHistories, setImportHistories] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedImport, setSelectedImport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rollbackModal, setRollbackModal] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 20,
  });
  const isUnmounted = useRef(false);

  const loadImportHistories = useCallback(
    async (page = 1) => {
      if (!selectedShopId) return;

      try {
        setLoading(true);
        fetchData(
          `/api/shops/${selectedShopId}/hds_import_histories?page=${page}`,
          (response) => {
            if (!isUnmounted.current) {
              setImportHistories(response.import_histories);
              setPagination(response.pagination);
              setLoading(false);
            }
          },
          (err) => {
            if (!isUnmounted.current) {
              setError("Failed to load import histories");
              console.error(err);
              setLoading(false);
            }
          }
        );
      } catch (err) {
        setError("Failed to load import histories");
        console.error(err);
        setLoading(false);
      }
    },
    [selectedShopId]
  );

  const loadStats = useCallback(async () => {
    if (!selectedShopId) return;

    try {
      fetchData(
        `/api/shops/${selectedShopId}/hds_import_histories/stats`,
        (response) => {
          if (!isUnmounted.current) {
            setStats(response.stats);
          }
        },
        (err) => {
          if (!isUnmounted.current) {
            console.error("Failed to load stats:", err);
          }
        }
      );
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  }, [selectedShopId]);

  useEffect(() => {
    if (selectedShopId) {
      loadImportHistories();
      loadStats();
    }
  }, [selectedShopId, loadImportHistories, loadStats]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isUnmounted.current = true;
    };
  }, []);

  const handleRollback = async () => {
    if (!selectedImport || !selectedShopId) return;

    try {
      setRollbackLoading(true);
      postData(
        `/api/shops/${selectedShopId}/hds_import_histories/${selectedImport.id}/rollback`
      )
        .then((response) => {
          if (!isUnmounted.current) {
            // Refresh data
            loadImportHistories(pagination.current_page);
            loadStats();
            setRollbackModal(false);
            setSelectedImport(null);
            setRollbackLoading(false);
          }
        })
        .catch((err) => {
          if (!isUnmounted.current) {
            setError("Failed to rollback import");
            console.error(err);
            setRollbackLoading(false);
          }
        });
    } catch (err) {
      setError("Failed to rollback import");
      console.error(err);
      setRollbackLoading(false);
    }
  };

  const handleImportClick = async (importHistory) => {
    try {
      setDetailLoading(true);
      // Fetch detailed import history data
      fetchData(
        `/api/shops/${selectedShopId}/hds_import_histories/${importHistory.id}`,
        (response) => {
          // Check if component is still mounted before updating state
          if (!isUnmounted.current) {
            setSelectedImport(response.import_history);
            setActiveTab(1);
            setDetailLoading(false);
          }
        },
        (err) => {
          // Check if component is still mounted before updating state
          if (!isUnmounted.current) {
            setError("Failed to load import details");
            console.error(err);
            setDetailLoading(false);
          }
        }
      );
    } catch (err) {
      // Check if component is still mounted before updating state
      if (!isUnmounted.current) {
        setError("Failed to load import details");
        console.error(err);
        setDetailLoading(false);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: "success", text: "Completed" },
      failed: { color: "critical", text: "Failed" },
      processing: { color: "warning", text: "Processing" },
      pending: { color: "subdued", text: "Pending" },
      rolled_back: { color: "info", text: "Rolled Back" },
    };

    const config = statusConfig[status] || { color: "subdued", text: status };
    return config.text;
  };

  if (loading) {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const tabs = [
    {
      id: "import-history",
      content: "Import History",
      accessibilityLabel: "Import History",
      panelID: "import-history-panel",
    },
  ];

  if (selectedImport) {
    tabs.push(
      {
        id: "import-details",
        content: "Import Details",
        accessibilityLabel: "Import Details",
        panelID: "import-details-panel",
      },
      {
        id: "changes",
        content: "Changes",
        accessibilityLabel: "Changes",
        panelID: "changes-panel",
      },
      {
        id: "errors",
        content: "Errors",
        accessibilityLabel: "Errors",
        panelID: "errors-panel",
      }
    );
  }

  return (
    <Page
      title="HDS Import History"
      subtitle="Track and manage your HDS product imports"
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner
              title="Error"
              status="critical"
              onDismiss={() => setError(null)}
            >
              {error}
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <Tabs tabs={tabs} selected={activeTab} onSelect={setActiveTab}>
              <Card.Section>
                {activeTab === 0 && (
                  <>
                    <ImportStats stats={stats} />
                    <ImportHistoryTable
                      importHistories={importHistories}
                      pagination={pagination}
                      onImportClick={handleImportClick}
                      onPageChange={loadImportHistories}
                      getStatusBadge={getStatusBadge}
                    />
                  </>
                )}

                {selectedImport &&
                  activeTab === 1 &&
                  (detailLoading ? (
                    <div style={{ textAlign: "center", padding: "2rem" }}>
                      <Spinner size="large" />
                    </div>
                  ) : (
                    <ImportDetails
                      importHistory={selectedImport}
                      onRollback={() => setRollbackModal(true)}
                    />
                  ))}

                {selectedImport &&
                  activeTab === 2 &&
                  (detailLoading ? (
                    <div style={{ textAlign: "center", padding: "2rem" }}>
                      <Spinner size="large" />
                    </div>
                  ) : (
                    <ChangeSummary importHistory={selectedImport} />
                  ))}

                {selectedImport &&
                  activeTab === 3 &&
                  (detailLoading ? (
                    <div style={{ textAlign: "center", padding: "2rem" }}>
                      <Spinner size="large" />
                    </div>
                  ) : (
                    <ErrorSummary importHistory={selectedImport} />
                  ))}
              </Card.Section>
            </Tabs>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Rollback Confirmation Modal */}
      <Modal
        open={rollbackModal}
        onClose={() => setRollbackModal(false)}
        title="Confirm Rollback"
        primaryAction={{
          content: rollbackLoading ? "Rolling back..." : "Rollback Import",
          destructive: true,
          loading: rollbackLoading,
          onAction: handleRollback,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => setRollbackModal(false),
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>
              Are you sure you want to rollback the import "
              {selectedImport?.filename}"?
            </p>
            <p>
              <strong>Warning:</strong> This will restore all products to their
              state before this import. This action cannot be undone.
            </p>
          </TextContainer>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default HdsImportHistoryPage;
